import { Injectable, inject } from '@angular/core';
import moment from 'moment';
import { HdChartSettingsStore } from '../chart/bodygraph/chart-tabs/hd-chart-settings.store';
import { HdContentLanguageService } from '../chart/bodygraph/hd-content-language.service';
import { HdPlanetInfoService } from '../chart/bodygraph/hd-planet-info.service';
import { HD_PLANET_ORDER_STANDARD, HdPlanetKey, getHdPlanetDisplayConfig } from '../chart/bodygraph/planet-grid/hd-planet-display-config';
import { formatGateLine } from '../transits/transit-countdown.utils';
import { TransitDataService } from '../transits/transit-data.service';
import { HdTransitPosition } from '../transits/transit.models';
import { MdPlanetRow } from './hd-export-planets.markdown';

/**
 * Planeetstanden voor exports: één batch-opvraging voor alle tijdstippen (op de minuut,
 * dubbels samengevoegd), rijen in de vaste HD-planeetvolgorde met namen in de content-taal.
 * Chiron telt mee zoals in het transitblok van de I Tjing (instelling showChiron).
 */
@Injectable({ providedIn: 'root' })
export class HdExportPlanetsService {
  private readonly transitData = inject(TransitDataService);
  private readonly settings = inject(HdChartSettingsStore);
  private readonly planetInfo = inject(HdPlanetInfoService);
  private readonly contentLang = inject(HdContentLanguageService);

  /** Rijen per tijdstip (ISO, UTC), in dezelfde volgorde als `atUtc`. */
  async rowsAt(atUtc: readonly string[]): Promise<MdPlanetRow[][]> {
    if (atUtc.length === 0) return [];
    const keys: readonly HdPlanetKey[] = this.settings.showChiron() ? [...HD_PLANET_ORDER_STANDARD, 'chiron'] : HD_PLANET_ORDER_STANDARD;
    const minutes = atUtc.map(at => moment.utc(at).startOf('minute').toISOString());
    const unique = [...new Set(minutes)];
    const positions = await this.transitData.getPositionsBatch(unique.map(at => moment.utc(at)), keys);
    const byMinute = new Map(unique.map((at, index) => [at, positions[index] ?? []]));
    const lang = this.contentLang.effectiveLang();
    return minutes.map(at => this.toRows(byMinute.get(at) ?? [], keys, lang));
  }

  private toRows(positions: readonly HdTransitPosition[], keys: readonly HdPlanetKey[], lang: string): MdPlanetRow[] {
    const byKey = new Map(positions.filter(position => position.planetKey != null).map(position => [position.planetKey, position]));
    return keys
      .map((key): MdPlanetRow | undefined => {
        const position = byKey.get(key);
        if (!position) return undefined;
        return {
          name: this.planetInfo.planetInfoIn(key, lang)?.name || getHdPlanetDisplayConfig(key).label,
          value: formatGateLine(position.gate, position.line),
          retrograde: position.retrograde,
        };
      })
      .filter((row): row is MdPlanetRow => row !== undefined);
  }
}
